import React, { useState, useEffect } from 'react';
import { Video, Cpu, Eye, Shield, Camera, HardDrive, CheckCircle, Bell, Wifi, Moon, Sun, Play, X, Zap, Focus, Wrench } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ShopCategoryNav from '../components/ShopCategoryNav';
import VideoEducationSection from '../components/VideoEducationSection';
import { Button } from '../components/ui/button';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Video URLs for technology showcase
const HYBRID_LIGHT_VIDEO = "https://customer-assets.emergentagent.com/job_7e2cbad7-4435-4a2d-9447-5a78a5627e25/artifacts/kkgn4v2o_smart_hybrid_sm_3cc6e763a7.mp4";
const VARIFOCAL_VIDEO = "https://customer-assets.emergentagent.com/job_7e2cbad7-4435-4a2d-9447-5a78a5627e25/artifacts/wv1f2zjo_s_cameras_varifocal_lens_8eaef228e8.mp4";

const VideoPage = () => {
  const [cameras, setCameras] = useState([]);
  const [doorbells, setDoorbells] = useState([]);
  const [nvrs, setNvrs] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'cameras', 'doorbells', 'nvr'
  const [typeFilter, setTypeFilter] = useState('all');
  const [techFilter, setTechFilter] = useState('all'); // 'all', 'ir', 'hl'
  const [resolutionFilter, setResolutionFilter] = useState('all'); // 'all', '5mp', '8mp'
  const [showVideoModal, setShowVideoModal] = useState(null); // 'hybrid' or 'varifocal'

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API}/products?category=video_basic`);
        if (response.ok) {
          const data = await response.json();
          setCameras(data.filter(p => p.subcategory === 'cameras'));
          setDoorbells(data.filter(p => p.subcategory === 'doorbells'));
          setNvrs(data.filter(p => p.subcategory === 'nvr'));
          setAccessories(data.filter(p => p.subcategory === 'mount' || p.subcategory === 'storage'));
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filterCameras = (cameras) => {
    let filtered = cameras;
    
    // Filter by type
    if (typeFilter !== 'all') {
      if (typeFilter === 'turret') filtered = filtered.filter(c => c.name.toLowerCase().includes('turretcam'));
      else if (typeFilter === 'bullet') filtered = filtered.filter(c => c.name.toLowerCase().includes('bulletcam'));
      else if (typeFilter === 'dome') filtered = filtered.filter(c => c.name.toLowerCase().includes('domecam'));
      else if (typeFilter === 'indoor') filtered = filtered.filter(c => c.name.toLowerCase().includes('indoorcam'));
    }
    
    // Filter by technology (IR / HL fixe / HLVF varifocal)
    // Important : HLVF contient "HL" dans le nom donc on doit l'exclure explicitement
    if (techFilter === 'ir') {
      filtered = filtered.filter(c => !c.name.includes('HL'));
    } else if (techFilter === 'hl') {
      filtered = filtered.filter(c => c.name.includes('HL') && !c.name.includes('HLVF'));
    } else if (techFilter === 'hlvf') {
      filtered = filtered.filter(c => c.name.includes('HLVF'));
    }
    
    // Filter by resolution
    if (resolutionFilter === '5mp') {
      filtered = filtered.filter(c => c.name.includes('5 Mp'));
    } else if (resolutionFilter === '8mp') {
      filtered = filtered.filter(c => c.name.includes('8 Mp'));
    }
    
    return filtered;
  };

  const features = [
    { icon: Cpu, title: 'Intelligence Artificielle', desc: 'Détection humains, animaux et véhicules' },
    { icon: Eye, title: 'Vision nocturne', desc: 'IR classique ou Hybrid Light couleur' },
    { icon: Shield, title: 'Protection IP67', desc: 'Usage extérieur intensif' },
    { icon: Camera, title: 'Résolution 4K', desc: 'Images 8MP ultra détaillées' },
  ];

  const typeOptions = [
    { key: 'all', label: 'Toutes' },
    { key: 'turret', label: 'TurretCam' },
    { key: 'bullet', label: 'BulletCam' },
    { key: 'dome', label: 'DomeCam' },
    { key: 'indoor', label: 'IndoorCam' },
  ];

  const filteredCameras = filterCameras(cameras);
  
  // Count cameras by technology (HLVF exclu de HL pour éviter double comptage)
  const irCount = cameras.filter(c => !c.name.includes('HL')).length;
  const hlCount = cameras.filter(c => c.name.includes('HL') && !c.name.includes('HLVF')).length;
  const mp5Count = cameras.filter(c => c.name.includes('5 Mp')).length;
  const mp8Count = cameras.filter(c => c.name.includes('8 Mp')).length;

  return (
    <div className="min-h-screen py-12 bg-black" data-testid="video-page">
      {/* Shop Category Navigation (Alarme / Vidéo / Incendie) */}
      <div className="pt-2">
        <ShopCategoryNav />
      </div>

      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-12">
        <div className="relative py-16 rounded-2xl md:rounded-3xl overflow-hidden bg-[#0a0a0a] border border-white/5">
          <div className="relative text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
              <Cpu className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">Baseline — Vidéosurveillance Ajax</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
              Baseline Vidéoprotection IA
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8 font-light">
              Caméras, sonnettes et enregistreurs Ajax avec IA — Jusqu'à -20%
            </p>
            
            {/* Category Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setCategoryFilter('all')}
                data-testid="category-all"
                className={`px-5 py-2.5 rounded-full font-medium transition-all ${
                  categoryFilter === 'all'
                    ? 'bg-white text-black'
                    : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20'
                }`}
              >
                Tous les produits
              </button>
              <button
                onClick={() => setCategoryFilter('cameras')}
                data-testid="category-cameras"
                className={`px-5 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                  categoryFilter === 'cameras'
                    ? 'bg-white text-black'
                    : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20'
                }`}
              >
                <Camera className="w-4 h-4" />
                Caméras ({cameras.length})
              </button>
              <button
                onClick={() => setCategoryFilter('doorbells')}
                data-testid="category-doorbells"
                className={`px-5 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                  categoryFilter === 'doorbells'
                    ? 'bg-white text-black'
                    : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20'
                }`}
              >
                <Bell className="w-4 h-4" />
                Sonnettes ({doorbells.length})
              </button>
              <button
                onClick={() => setCategoryFilter('nvr')}
                data-testid="category-nvr"
                className={`px-5 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                  categoryFilter === 'nvr'
                    ? 'bg-white text-black'
                    : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20'
                }`}
              >
                <HardDrive className="w-4 h-4" />
                NVR ({nvrs.length})
              </button>
              <button
                onClick={() => setCategoryFilter('accessories')}
                data-testid="category-accessories"
                className={`px-5 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                  categoryFilter === 'accessories'
                    ? 'bg-white text-black'
                    : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20'
                }`}
              >
                <Wrench className="w-4 h-4" />
                Accessoires ({accessories.length})
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Exclusivité Garantie 4 ans */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-8">
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="text-white/70">
                <span className="font-medium text-emerald-400">Exclusivité FGS</span> — Nous garantissons <strong className="text-white">tous les produits Ajax pendant 4 ans</strong>, 
                soit 2 ans de plus que la garantie constructeur standard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section éducative : Pourquoi Ajax + IA + écosystème */}
      {(categoryFilter === 'all' || categoryFilter === 'cameras') && (
        <VideoEducationSection />
      )}

      {/* Technology Selector — 3 catégories (IR / HL / HLVF) */}
      {(categoryFilter === 'all' || categoryFilter === 'cameras') && (
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-12">
        <h2 className="text-2xl md:text-3xl font-semibold text-white uppercase mb-2 text-center tracking-tight">
          Choisissez votre technologie
        </h2>
        <p className="text-center text-white/50 mb-8 text-sm">
          3 familles de caméras Ajax — sélectionnez celle qui correspond à votre besoin
        </p>

        <div className="grid md:grid-cols-3 gap-5">
          {/* IR */}
          <button
            onClick={() => setTechFilter(techFilter === 'ir' ? 'all' : 'ir')}
            data-testid="tech-card-ir"
            className={`text-left bg-white/[0.02] border rounded-2xl overflow-hidden transition-all ${
              techFilter === 'ir'
                ? 'border-blue-500 ring-2 ring-blue-500/30'
                : 'border-white/5 hover:border-blue-500/40'
            }`}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Moon className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Caméras IR</h3>
                  <span className="text-blue-400 text-xs uppercase tracking-wide">Objectif fixe · {irCount} modèles</span>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Vision nocturne classique en <strong className="text-white">noir & blanc</strong>. Objectif fixe (2.8mm ou 4mm). Le plus discret et économique.
              </p>
              <div className="space-y-1.5 text-xs text-white/50">
                <div>✓ LED IR invisibles jusqu'à 50m</div>
                <div>✓ Idéal surveillance discrète</div>
                <div>✓ Prix le plus accessible</div>
              </div>
            </div>
            <div className={`py-2.5 text-center text-xs font-semibold uppercase tracking-wider ${
              techFilter === 'ir' ? 'bg-blue-500 text-white' : 'bg-blue-500/10 text-blue-400'
            }`}>
              {techFilter === 'ir' ? '✓ Sélectionné' : 'Voir les caméras IR →'}
            </div>
          </button>

          {/* HL */}
          <button
            onClick={() => setTechFilter(techFilter === 'hl' ? 'all' : 'hl')}
            data-testid="tech-card-hl"
            className={`text-left bg-white/[0.02] border rounded-2xl overflow-hidden transition-all relative ${
              techFilter === 'hl'
                ? 'border-purple-500 ring-2 ring-purple-500/30'
                : 'border-white/5 hover:border-purple-500/40'
            }`}
          >
            <div className="absolute top-3 right-3 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-[10px] font-bold text-white">
              POPULAIRE
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Sun className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Caméras HL</h3>
                  <span className="text-purple-400 text-xs uppercase tracking-wide">Objectif fixe · Vision couleur · {hlCount} modèles</span>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                <strong className="text-purple-300">Vision nuit en couleur</strong> grâce aux LED blanches activées sur détection. Objectif fixe. Effet dissuasif maximum.
              </p>
              <div className="space-y-1.5 text-xs text-white/50">
                <div>✓ Identification des couleurs de nuit</div>
                <div>✓ LED blanches activées sur mouvement</div>
                <div>✓ Dissuasion visible</div>
              </div>
            </div>
            <div className={`py-2.5 text-center text-xs font-semibold uppercase tracking-wider ${
              techFilter === 'hl' ? 'bg-purple-500 text-white' : 'bg-purple-500/10 text-purple-400'
            }`}>
              {techFilter === 'hl' ? '✓ Sélectionné' : 'Voir les caméras HL →'}
            </div>
          </button>

          {/* HLVF */}
          <button
            onClick={() => setTechFilter(techFilter === 'hlvf' ? 'all' : 'hlvf')}
            data-testid="tech-card-hlvf"
            className={`text-left bg-white/[0.02] border rounded-2xl overflow-hidden transition-all relative ${
              techFilter === 'hlvf'
                ? 'border-amber-500 ring-2 ring-amber-500/30'
                : 'border-white/5 hover:border-amber-500/40'
            }`}
          >
            <div className="absolute top-3 right-3 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full text-[10px] font-bold text-white">
              PREMIUM
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Caméras HLVF</h3>
                  <span className="text-amber-400 text-xs uppercase tracking-wide">Zoom motorisé · Vision couleur</span>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Vision couleur Hybrid Light + <strong className="text-amber-300">zoom optique motorisé 2.8–12mm</strong> réglable à distance via l'app Ajax.
              </p>
              <div className="space-y-1.5 text-xs text-white/50">
                <div>✓ Zoom optique (pas numérique)</div>
                <div>✓ Réglage focale à distance</div>
                <div>✓ Polyvalence maximale</div>
              </div>
            </div>
            <div className={`py-2.5 text-center text-xs font-semibold uppercase tracking-wider ${
              techFilter === 'hlvf' ? 'bg-amber-500 text-white' : 'bg-amber-500/10 text-amber-400'
            }`}>
              {techFilter === 'hlvf' ? '✓ Sélectionné' : 'Voir les caméras HLVF →'}
            </div>
          </button>
        </div>

        {/* Comparatif compact */}
        <div className="mt-8 bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-white font-semibold uppercase text-sm tracking-wide">Comparatif rapide</h3>
            <button
              onClick={() => setShowVideoModal('varifocal')}
              className="text-amber-400 hover:text-amber-300 text-xs flex items-center gap-1"
            >
              <Play className="w-3 h-3" /> Voir le zoom motorisé en vidéo
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 text-white/40 text-xs uppercase tracking-wider">
                  <th className="text-left py-3 px-6 font-medium">Caractéristique</th>
                  <th className="py-3 px-4 font-medium text-blue-400">IR</th>
                  <th className="py-3 px-4 font-medium text-purple-400">HL</th>
                  <th className="py-3 px-4 font-medium text-amber-400">HLVF</th>
                </tr>
              </thead>
              <tbody className="text-white/70">
                <tr className="border-b border-white/5">
                  <td className="py-3 px-6 text-white">Vision nocturne</td>
                  <td className="text-center py-3 px-4">Noir & blanc</td>
                  <td className="text-center py-3 px-4 text-purple-300">Couleur 24h/24</td>
                  <td className="text-center py-3 px-4 text-amber-300">Couleur 24h/24</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-6 text-white">Objectif</td>
                  <td className="text-center py-3 px-4">Fixe (2.8 ou 4mm)</td>
                  <td className="text-center py-3 px-4">Fixe (2.8 ou 4mm)</td>
                  <td className="text-center py-3 px-4 text-amber-300">Motorisé 2.8–12mm</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-6 text-white">Effet dissuasif</td>
                  <td className="text-center py-3 px-4 text-white/40">Faible (invisible)</td>
                  <td className="text-center py-3 px-4">Élevé (LED visibles)</td>
                  <td className="text-center py-3 px-4">Élevé (LED visibles)</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-6 text-white">Réglage à distance</td>
                  <td className="text-center py-3 px-4 text-white/40">—</td>
                  <td className="text-center py-3 px-4 text-white/40">—</td>
                  <td className="text-center py-3 px-4 text-emerald-400">Oui (zoom & focale)</td>
                </tr>
                <tr>
                  <td className="py-3 px-6 text-white">Usage idéal</td>
                  <td className="text-center py-3 px-4 text-xs">Surveillance discrète</td>
                  <td className="text-center py-3 px-4 text-xs">Reconnaissance visuelle</td>
                  <td className="text-center py-3 px-4 text-xs">Zones polyvalentes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Reset filter */}
        {techFilter !== 'all' && (
          <div className="text-center mt-6">
            <button
              onClick={() => setTechFilter('all')}
              className="text-white/60 hover:text-white text-sm underline"
              data-testid="reset-tech-filter"
            >
              Voir toutes les technologies
            </button>
          </div>
        )}
      </section>
      )}


      {/* Cameras Section with Enhanced Filters */}
      {(categoryFilter === 'all' || categoryFilter === 'cameras') && (
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="flex flex-col gap-4 mb-8">
          {/* Title */}
          <div className="flex items-center gap-3">
            <Camera className="w-8 h-8 text-purple-400" />
            <h2 className="text-3xl font-semibold text-white uppercase">
              Caméras Baseline
            </h2>
          </div>
          
          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Type Filter */}
            <div className="flex flex-wrap gap-2">
              <span className="text-white/60 text-sm self-center mr-1">Type :</span>
              {typeOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setTypeFilter(opt.key)}
                  data-testid={`filter-type-${opt.key}`}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    typeFilter === opt.key
                      ? 'bg-purple-500 text-white'
                      : 'bg-white/[0.02] border border-white/5 text-white/70 hover:text-white hover:border-purple-500/50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            
            {/* Separator */}
            <div className="hidden md:block w-px h-8 bg-fgs-border" />
            
            {/* Resolution Filter */}
            <div className="flex gap-2">
              <span className="text-white/60 text-sm self-center mr-1">Résolution :</span>
              <button
                onClick={() => setResolutionFilter(resolutionFilter === 'all' ? '5mp' : resolutionFilter === '5mp' ? '8mp' : 'all')}
                data-testid="filter-resolution"
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  resolutionFilter !== 'all'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white/[0.02] border border-white/5 text-white/70 hover:text-white hover:border-emerald-500/30'
                }`}
              >
                {resolutionFilter === 'all' ? 'Toutes' : resolutionFilter === '5mp' ? '5MP' : '8MP (4K)'}
              </button>
              <button
                onClick={() => setResolutionFilter('5mp')}
                data-testid="filter-5mp"
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  resolutionFilter === '5mp'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white/[0.02] border border-white/5 text-white/70 hover:text-white hover:border-emerald-500/30'
                }`}
              >
                5MP ({mp5Count})
              </button>
              <button
                onClick={() => setResolutionFilter('8mp')}
                data-testid="filter-8mp"
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                  resolutionFilter === '8mp'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white/[0.02] border border-white/5 text-white/70 hover:text-white hover:border-emerald-500/30'
                }`}
              >
                8MP 4K ({mp8Count})
              </button>
            </div>
          </div>
          
          {/* Active Filters Summary */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-white/60">{filteredCameras.length} produits</span>
            {(techFilter !== 'all' || resolutionFilter !== 'all' || typeFilter !== 'all') && (
              <>
                <span className="text-white/60">—</span>
                {techFilter !== 'all' && (
                  <span className={`px-2 py-0.5 rounded ${techFilter === 'hl' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                    {techFilter === 'hl' ? 'Hybrid Light' : 'Infrarouge'}
                  </span>
                )}
                {resolutionFilter !== 'all' && (
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                    {resolutionFilter.toUpperCase()}
                  </span>
                )}
                <button 
                  onClick={() => { setTechFilter('all'); setResolutionFilter('all'); setTypeFilter('all'); }}
                  className="text-white/60 hover:text-white underline ml-2"
                >
                  Réinitialiser
                </button>
              </>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl h-96 animate-pulse" />
            ))}
          </div>
        ) : filteredCameras.length === 0 ? (
          <div className="text-center py-12 bg-white/[0.02] border border-white/5 rounded-xl">
            <Camera className="w-12 h-12 text-white/50 mx-auto mb-4" />
            <p className="text-white/60">Aucune caméra ne correspond aux filtres sélectionnés</p>
            <button 
              onClick={() => { setTechFilter('all'); setResolutionFilter('all'); setTypeFilter('all'); }}
              className="mt-4 text-emerald-400 hover:underline"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCameras.map((product) => (
              <ProductCard key={product.product_id} product={product} />
            ))}
          </div>
        )}
      </section>
      )}

      {/* Doorbells Section */}
      {(categoryFilter === 'all' || categoryFilter === 'doorbells') && doorbells.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
          <div className="flex items-center gap-3 mb-8">
            <Bell className="w-8 h-8 text-purple-400" />
            <h2 className="text-3xl font-semibold text-white uppercase">
              Sonnettes Vidéo
            </h2>
            <span className="ml-auto text-white/60">{doorbells.length} produits</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {doorbells.map((product) => (
              <ProductCard key={product.product_id} product={product} />
            ))}
          </div>
        </section>
      )}

      {/* NVR Section */}
      {(categoryFilter === 'all' || categoryFilter === 'nvr') && (
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="flex items-center gap-3 mb-8">
          <HardDrive className="w-8 h-8 text-purple-400" />
          <h2 className="text-3xl font-semibold text-white uppercase">
            Enregistreurs NVR
          </h2>
          <span className="ml-auto text-white/60">{nvrs.length} produits</span>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl h-96 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nvrs.map((product) => (
              <ProductCard key={product.product_id} product={product} />
            ))}
          </div>
        )}
      </section>
      )}

      {/* Accessoires Section : Supports MountCam + JunctionBox + Cartes SD */}
      {(categoryFilter === 'all' || categoryFilter === 'accessories') && accessories.length > 0 && (
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16" data-testid="accessories-section">
        <div className="flex items-center gap-3 mb-3">
          <Wrench className="w-8 h-8 text-amber-400" />
          <h2 className="text-3xl font-semibold text-white uppercase">
            Accessoires
          </h2>
          <span className="ml-auto text-white/60">{accessories.length} produits</span>
        </div>
        <p className="text-white/50 text-sm mb-8">
          Supports muraux MountCam, JunctionBox étanche et cartes microSD Hikvision — pour compléter votre installation vidéo Ajax.
        </p>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl h-96 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {accessories.map((product) => (
              <ProductCard key={product.product_id} product={product} />
            ))}
          </div>
        )}
      </section>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors z-10"
            onClick={() => setShowVideoModal(null)}
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 text-center">
              <h3 className="text-xl font-bold text-white mb-2">
                {showVideoModal === 'hybrid' ? 'Technologie Hybrid Light' : 'Objectifs 2.8mm vs 4mm'}
              </h3>
              <p className="text-white/60">
                {showVideoModal === 'hybrid' 
                  ? 'Découvrez la vision nocturne en couleur avec les caméras Hybrid Light'
                  : 'Comparez les champs de vision selon la focale de l\'objectif'
                }
              </p>
            </div>
            
            <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
              <video 
                src={showVideoModal === 'hybrid' ? HYBRID_LIGHT_VIDEO : VARIFOCAL_VIDEO}
                controls
                autoPlay
                className="w-full h-full"
              >
                Votre navigateur ne supporte pas la lecture vidéo.
              </video>
            </div>
            
            <div className="mt-4 text-center">
              <button 
                onClick={() => setShowVideoModal(null)}
                className="px-6 py-2 bg-emerald-500 hover:bg-emerald-500-hover text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPage;
